<template>
  <b-container>
    <div class="d-flex align-items-center justify-content-between mb-2">
      <div>
        <h2 class="mb-0">Parâmetros do monitoramento de cedentes</h2>
      </div>
    </div>

    <div v-if="classifications.length" class="mb-2">
      <b-badge
        v-for="classification in classifications"
        :key="classification.id"
        variant="primary"
        class="mr-1"
      >
        {{ classification.name }}: {{ classification.min_score }}–{{ classification.max_score }}
      </b-badge>
    </div>

    <b-form-group label="Classificação do limite de crédito" label-for="credit-classification">
      <b-form-select
        id="credit-classification"
        v-model="selectedClassification"
        :options="classificationOptions"
        class="w-auto"
        :disabled="loading"
      />
    </b-form-group>

    <b-card v-for="parameter in parameters" :key="parameter.id" class="mb-2">
      <div class="d-flex align-items-center justify-content-between mb-2">
        <div>
          <h4 class="mb-0">{{ parameterLabel(parameter) }}</h4>
        </div>
        <b-button
          size="sm"
          variant="outline-primary"
          :disabled="loading || newRuleParameterId === parameter.source_parameter_id"
          @click="startNewRule(parameter)"
        >
          Adicionar regra
        </b-button>
      </div>

      <div v-if="rulesForParameter(parameter).length">
        <b-row class="text-muted small font-weight-bold mb-1 d-none d-lg-flex">
          <b-col lg="2">Métrica</b-col>
          <b-col lg="2">Operador</b-col>
          <b-col lg="2">Efeito</b-col>
          <b-col lg="2">Limite </b-col>
          <b-col lg="2">Penalidade</b-col>
          <b-col lg="2" />
        </b-row>

        <b-row
          v-for="rule in rulesForParameter(parameter)"
          :key="rule.id"
          class="align-items-center border-top py-2"
        >
          <b-col cols="12" lg="2" class="mb-1 mb-lg-0">
            <span class="d-lg-none text-muted">Métrica: </span>{{ metricLabel(rule.metric) }}
          </b-col>
          <b-col cols="6" lg="2" class="mb-1 mb-lg-0">
            <b-form-select
              v-model="rule.operator"
              :options="operatorOptionsForMetric(rule.metric)"
              size="sm"
              @change="onExistingOperatorChange(rule)"
            />
          </b-col>
          <b-col cols="6" lg="2" class="mb-1 mb-lg-0">
            <b-form-select v-model="rule.effect" :options="effectOptions" size="sm" />
          </b-col>
          <b-col cols="6" lg="2" class="mb-1 mb-lg-0">
            <div class="input-group input-group-sm">
              <b-form-input
                v-model="rule.threshold"
                type="text"
                inputmode="decimal"
                placeholder="Limite"
                :disabled="isBooleanOperator(rule.operator)"
                @focus="selectZero($event, rule.threshold)"
                @blur="formatNumericField(rule, 'threshold')"
              />

            </div>
          </b-col>
          <b-col cols="6" lg="2" class="mb-1 mb-lg-0">
            <div class="input-group input-group-sm">
              <b-form-input
                v-model="rule.penalty"
                type="text"
                inputmode="numeric"
                placeholder="Penalidade"
                @blur="validatePenalty(rule)"
              />
            </div>
          </b-col>
          <b-col cols="12" lg="2" class="text-lg-right">
            <b-button
              size="sm"
              variant="primary"
              :disabled="loading || !isRuleValid(rule) || !isRuleDirty(rule)"
              @click="saveRule(rule)"
            >
              Salvar
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              class="ml-1"
              :disabled="loading"
              @click="deleteRule(rule)"
            >
              Excluir
            </b-button>
          </b-col>
        </b-row>
      </div>
      <p v-else class="text-muted mb-0">Nenhuma regra para esta classificação.</p>

      <div v-if="newRuleParameterId === parameter.source_parameter_id" class="border-top mt-2 pt-2">
        <h5>Nova regra</h5>
        <b-row class="align-items-end">
          <b-col cols="12" md="6" lg="3">
            <b-form-group label="Métrica">
              <b-form-select
                v-model="newRule.metric"
                :options="metricOptions"
                size="sm"
                @change="onNewMetricChange"
              />
            </b-form-group>
          </b-col>
          <b-col cols="6" md="3" lg="2">
            <b-form-group label="Operador">
              <b-form-select
                v-model="newRule.operator"
                :options="operatorOptionsForMetric(newRule.metric)"
                size="sm"
                @change="onNewOperatorChange"
              />
            </b-form-group>
          </b-col>
          <b-col cols="6" md="3" lg="2">
            <b-form-group label="Efeito">
              <b-form-select v-model="newRule.effect" :options="effectOptions" size="sm" />
            </b-form-group>
          </b-col>
          <b-col cols="6" md="3" lg="2">
            <b-form-group label="Limite ">
              <div class="input-group input-group-sm">
                <b-form-input
                  v-model="newRule.threshold"
                  type="text"
                  inputmode="decimal"
                  :disabled="isBooleanOperator(newRule.operator)"
                  @focus="selectZero($event, newRule.threshold)"
                  @blur="formatNumericField(newRule, 'threshold')"
                />
              </div>
            </b-form-group>
          </b-col>
          <b-col cols="6" md="3" lg="2">
            <b-form-group label="Penalidade">
              <div class="input-group input-group-sm">
                <b-form-input
                  v-model="newRule.penalty"
                  type="text"
                  inputmode="numeric"
                  @blur="validatePenalty(newRule)"
                />
              </div>
            </b-form-group>
          </b-col>
          <b-col cols="12" lg="1" class="mb-3">
            <b-button
              size="sm"
              variant="primary"
              :disabled="loading || !isRuleValid(newRule)"
              @click="createRule"
            >
              Criar
            </b-button>
          </b-col>
        </b-row>
        <b-button size="sm" variant="link" class="p-0" @click="cancelNewRule">
          Cancelar
        </b-button>
      </div>
    </b-card>
  </b-container>
</template>

<script>
import {
  BBadge, BButton, BCard, BCol, BContainer, BFormGroup, BFormInput, BFormSelect, BRow,
} from 'bootstrap-vue';

import MonitoringProfileService from '@/service/profile-monitoring';

const BOOLEAN_OPERATORS = ['boolean_true', 'boolean_false'];
const BOOLEAN_ONLY_METRICS = [
    'coobligation',
    'judicial_recovery',
    'guarantee',
    'account_blocked',
    'tranche',
    'injunction',
    'tac',
];

export default {
  components: {
    BBadge, BButton, BCard, BCol, BContainer, BFormGroup, BFormInput, BFormSelect, BRow,
  },

  data() {
    return {
      version: null,
      parameters: [],
      rules: [],
      classifications: [],
      originalRules: {},
      selectedClassification: 'A',
      newRuleParameterId: null,
      newRule: {},
      loading: false,
      classificationOptions: [
        { value: 'A', text: 'A' },
        { value: 'B', text: 'B' },
        { value: 'C', text: 'C' },
      ],
      operatorOptions: [
        { value: 'greater_than', text: 'Maior que' },
        { value: 'less_than', text: 'Menor que' },
        { value: 'equal', text: 'Igual a' },
        { value: 'boolean_true', text: 'Verdadeiro' },
        { value: 'boolean_false', text: 'Falso' },
      ],
      effectOptions: [
        { value: 'subtract', text: 'Subtrair' },
        { value: 'add', text: 'Somar' },
      ],
      metricOptions: [
        { value: 'overdue_risk_percentage', text: '% Atraso / risco' },
        { value: 'liquidity_percentage', text: 'Liquidez' },
        { value: 'extended_percentage', text: 'Prorrogação' },
        { value: 'rebuy_percentage', text: 'Recompra' },
        { value: 'volume_operated_90_days_percentage', text: 'Volume operado em 90 dias' },
        { value: 'delay_liquidation_percentage', text: 'Atraso na liquidação' },
        { value: 'refin', text: 'Refin' },
        { value: 'pefin_plus_divide_expired', text: 'Pefin + dívida vencida' },
        { value: 'protest', text: 'Protesto' },
        { value: 'coobligation', text: 'Coobrigação' },
        { value: 'judicial_recovery', text: 'Recuperação judicial' },
        { value: 'guarantee', text: 'Garantia' },
        { value: 'injunction', text: 'Liminar' },
        { value: 'account_blocked', text: 'Conta bloqueada' },
        { value: 'check_returned', text: 'Cheque devolvido' },
        { value: 'tranche', text: 'Tranche' },
        { value: 'tac', text: 'TAC' },
      ],
      paramsTranslated: {
        'Overdue Risk': '% Atraso / risco',
        Liquidity: 'Liquidez',
        Extended: 'Prorrogação',
        Rebuy: 'Recompra',
        'Volume Operated Last 90 Days': 'Volume operado dos últimos 90 dias',
        Protest: 'Protesto',
        'Volume Operated Monthly': 'Volume operado mensal',
        Coobligation: 'Coobrigação',
        'Judicial Recovery': 'Recuperação judicial',
        Guarantee: 'Garantia',
        'Returns Check': 'Cheque devolvido',
        'Account Blocked': 'Conta bloqueada',
        'Delay Liquidation': 'Atraso na liquidação',
        Injunction: 'Liminar',
        Injuction: 'Liminar',
        'Tranche Days': 'Dias de Tranche ',
      },
    };
  },

  computed: {
    filteredRules() {
      const selectedClassification = String(this.selectedClassification).trim().toUpperCase();
      const filteredRules = this.rules.filter(rule => (
        String(rule.classification || '').trim().toUpperCase() === selectedClassification
      ));
     

      return filteredRules;
    },
  },

  created() {
    this.getData();
  },

  methods: {
    async getData() {
      this.loading = true;
      this.callLoading(true);

      try {
        const response = await MonitoringProfileService.getParameters();
        if (!response || response.status >= 400) {
          this.$toast.error(this.responseError('Não foi possível carregar os parâmetros.'));
          return;
        }

        const { version, parameters, rules, classifications } = response.data;
        this.version = version;
        this.parameters = parameters || [];
        this.rules = (rules || []).map(rule => ({
          ...rule,
          classification: rule.classification || rule.credit_limit_classification,
          threshold: rule.threshold === null || rule.threshold === ''
            || typeof rule.threshold === 'undefined'
            ? '0'
            : this.formatNumber(rule.threshold),
          penalty: this.formatPenalty(rule.penalty),
        }));
        this.classifications = classifications || [];
        this.originalRules = this.rules.reduce((acc, rule) => {
          acc[rule.id] = {
            operator: rule.operator,
            effect: rule.effect,
            threshold: rule.threshold,
            penalty: rule.penalty,
          };
          return acc;
        }, {});
      } catch (error) {
        this.$toast.error('Não foi possível carregar os parâmetros.');
      } finally {
        this.loading = false;
        this.callLoading(false);
      }
    },

    rulesForParameter(parameter) {
      return this.filteredRules.filter(rule => (
        Number(rule.source_parameter_id) === Number(parameter.source_parameter_id)
      ));
    },

    parameterLabel(parameter) {
      return this.paramsTranslated[parameter.name] || parameter.name;
    },

    metricLabel(metric) {
      const option = this.metricOptions.find(item => item.value === metric);
      return option ? option.text : metric;
    },

    isBooleanOperator(operator) {
      return BOOLEAN_OPERATORS.includes(operator);
    },

    isBooleanOnlyMetric(metric) {
      return BOOLEAN_ONLY_METRICS.includes(metric);
    },

    operatorOptionsForMetric(metric) {
      return this.isBooleanOnlyMetric(metric)
        ? this.operatorOptions.filter(option => this.isBooleanOperator(option.value))
        : this.operatorOptions;
    },

    onExistingOperatorChange(rule) {
      if (this.numberOrNull(rule.threshold) === null) {
        rule.threshold = 0;
      }
    },

    onNewMetricChange() {
      if (this.isBooleanOnlyMetric(this.newRule.metric)) {
        if (!this.isBooleanOperator(this.newRule.operator)) {
          this.newRule.operator = BOOLEAN_OPERATORS[0];
        }
      } else if (this.isBooleanOperator(this.newRule.operator)) {
        this.newRule.operator = 'greater_than';
      }
    },

    onNewOperatorChange() {
      if (this.numberOrNull(this.newRule.threshold) === null) {
        this.newRule.threshold = 0;
      }
    },

    numberOrNull(value) {
      if (value === null || value === '' || typeof value === 'undefined') return null;
      const normalized = typeof value === 'string'
        ? value.trim().replace(/\s/g, '')
        : value;
      if (normalized === '') return null;

      const hasDecimalComma = typeof normalized === 'string' && normalized.includes(',');
      const isGroupedInteger = typeof normalized === 'string'
        && /^-?\d{1,3}(\.\d{3})+$/.test(normalized);
      const parsed = Number(typeof normalized === 'string' && hasDecimalComma
        ? normalized.replace(/\./g, '').replace(',', '.')
        : (isGroupedInteger ? normalized.replace(/\./g, '') : normalized));
      return Number.isFinite(parsed) ? parsed : NaN;
    },

    formatNumber(value) {
      if (value === null || value === '' || typeof value === 'undefined') return '';
      const number = Number(value);
      if (!Number.isFinite(number)) return '';

      return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 20 }).format(number);
    },

    formatPenalty(value) {
      if (value === null || value === '' || typeof value === 'undefined') return '';
      const number = this.numberOrNull(value);
      return Number.isFinite(number) ? String(number) : String(value);
    },

    selectZero(event, value) {
      if (this.numberOrNull(value) === 0) event.target.select();
    },

    formatNumericField(rule, field) {
      const number = this.numberOrNull(rule[field]);
      rule[field] = number === null ? '' : this.formatNumber(number);
    },

    isPenaltyValid(value) {
      const penalty = this.numberOrNull(value);
      return penalty !== null
        && Number.isInteger(penalty)
        && penalty >= 0
        && penalty <= 99;
    },

    validatePenalty(rule) {
      if (rule.penalty === null || rule.penalty === '') return;
      if (!this.isPenaltyValid(rule.penalty)) {
        this.$toast.error('A penalidade deve ser um número inteiro entre 0 e 99.');
      }
    },

    isRuleValid(rule) {
      const threshold = this.numberOrNull(rule.threshold);
      const thresholdValid = threshold !== null && Number.isFinite(threshold);

      return Boolean(rule.metric)
        && Boolean(rule.operator)
        && Boolean(rule.effect)
        && thresholdValid
        && this.isPenaltyValid(rule.penalty);
    },

    isRuleDirty(rule) {
      const original = this.originalRules[rule.id];
      if (!original) return false;

      return rule.operator !== original.operator
        || rule.effect !== original.effect
        || this.numberOrNull(rule.threshold) !== this.numberOrNull(original.threshold)
        || this.numberOrNull(rule.penalty) !== this.numberOrNull(original.penalty);
    },

    ruleActor() {
      const user = this.$store.state.user && this.$store.state.user.data;
      return user && (user.name || user.email);
    },

    async saveRule(rule) {
      const original = this.originalRules[rule.id];
      const data = {};
      if (rule.operator !== original.operator) data.operator = rule.operator;
      if (rule.effect !== original.effect) data.effect = rule.effect;
      if (
        this.numberOrNull(rule.threshold) !== this.numberOrNull(original.threshold)
        || (this.isBooleanOperator(rule.operator) && !this.isBooleanOperator(original.operator))
      ) {
        data.threshold = this.numberOrNull(rule.threshold);
      }
      if (this.numberOrNull(rule.penalty) !== this.numberOrNull(original.penalty)) {
        data.penalty = this.numberOrNull(rule.penalty);
      }

      await this.submitRuleUpdate(rule.id, data);
    },

    async submitRuleUpdate(ruleId, data) {
      this.loading = true;
      this.callLoading(true);

      try {
        const response = await MonitoringProfileService.updateRule(ruleId, data, this.ruleActor());
        if (!response || response.status >= 400) {
          this.$toast.error(this.responseError(response, 'Não foi possível atualizar a regra.'));
          return;
        }
        await this.getData();
        this.$toast.success('Regra atualizada com sucesso.');
      } catch (error) {
        this.$toast.error('Não foi possível atualizar a regra.');
      } finally {
        this.loading = false;
        this.callLoading(false);
      }
    },

    async deleteRule(rule) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Tem certeza que deseja excluir a regra "${this.metricLabel(rule.metric)}"?`,
        {
          title: 'Confirmar exclusão',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Excluir',
          cancelTitle: 'Cancelar',
          centered: true,
        },
      );

      if (!confirmed) return;

      this.loading = true;
      this.callLoading(true);

      try {
        const response = await MonitoringProfileService.deleteRule(rule.id, this.ruleActor());
        if (response && response.status === 409) {
          this.$toast.error('Esta regra já gerou eventos de pontuação e não pode ser excluída.');
          return;
        }
        if (!response || response.status >= 400) {
          this.$toast.error(this.responseError(response, 'Não foi possível excluir a regra.'));
          return;
        }
        await this.getData();
        this.$toast.success('Regra excluída com sucesso.');
      } catch (error) {
        this.$toast.error('Não foi possível excluir a regra.');
      } finally {
        this.loading = false;
        this.callLoading(false);
      }
    },

    startNewRule(parameter) {
      this.newRuleParameterId = parameter.source_parameter_id;
      this.newRule = {
        sourceParameterId: parameter.source_parameter_id,
        creditLimitClassification: this.selectedClassification,
        operator: 'greater_than',
        effect: 'subtract',
        threshold: 0,
        penalty: 0,
        metric: this.metricOptions[0].value,
      };
    },

    cancelNewRule() {
      this.newRuleParameterId = null;
      this.newRule = {};
    },

    async createRule() {
      const data = {
        ...this.newRule,
        threshold: this.numberOrNull(this.newRule.threshold),
        penalty: this.numberOrNull(this.newRule.penalty),
      };

      this.loading = true;
      this.callLoading(true);

      try {
        const response = await MonitoringProfileService.createRule(data, this.ruleActor());
        if (!response || response.status >= 400) {
          this.$toast.error(this.responseError(response, 'Não foi possível criar a regra.'));
          return;
        }
        this.cancelNewRule();
        await this.getData();
        this.$toast.success('Regra criada com sucesso.');
      } catch (error) {
        this.$toast.error('Não foi possível criar a regra.');
      } finally {
        this.loading = false;
        this.callLoading(false);
      }
    },

    
  responseError(response, fallback) {
    return fallback || 'Não foi possível realizar a operação. Tente novamente.';
  },
  },
};
</script>
